import {
    type GetCraTopicStatusesQuery,
    CraGeneralProductCategory,
    type CraCybersecRiskAssessmentUploadCompleteness,
} from '@gql/graphql';
import { type Maybe } from '@models/Maybe';
import { SectionStatus } from '@models/section-status';

export { SectionStatus };

export type TopicKey = Exclude<keyof GetCraTopicStatusesQuery, '__typename'>;

interface CraTopicConfig {
    title: string;
    slug: string;
}

export const CRA_TOPICS: Record<TopicKey, CraTopicConfig> = {
    general: {
        title: 'General Product Description & Support Period',
        slug: 'general',
    },
    documentation: {
        title: 'Documentations and References',
        slug: 'documentation',
    },
    vulnerability: {
        title: 'Vulnerability Handling Process',
        slug: 'vulnerability-handling',
    },
    risk: {
        title: 'Cybersecurity Risk Assessment',
        slug: 'cybersecurity-risk-assessment',
    },
};

export const CRA_TOPIC_KEYS = [
    'general',
    'documentation',
    'vulnerability',
    'risk',
] as const satisfies readonly TopicKey[];

export const getTopicTitle = (key: TopicKey): string => CRA_TOPICS[key].title;

export const getTopicTitleBySlug = (slug: string): string | undefined =>
    Object.values(CRA_TOPICS).find((topic) => topic.slug === slug)?.title;

export enum CraApplicability {
    YES = 'yes',
    NO = 'no',
}

export enum CommentAction {
    SAVED = 'saved',
    MarkedComplete = 'marked_complete',
    MarkedIncomplete = 'marked_incomplete',
    UPLOADED = 'uploaded',
    DELETED = 'deleted',
}

export enum MarkSectionMode {
    COMPLETE = 'complete',
    INCOMPLETE = 'incomplete',
}

export interface EvidenceItem {
    id: string;
    fileName: string;
    fileFormat: string;
    uploadedBy: string;
    uploadedAt: Date;
    updateAt?: Date;
    file?: File;
    completeness?: Maybe<CraCybersecRiskAssessmentUploadCompleteness>;
}

export interface CommentItem {
    id: string;
    author: string;
    createdAt: Date;
    text: string;
    action: CommentAction;
}

export interface TopicCard {
    id: string;
    title: string;
    completed: boolean;
    path?: string;
}

export interface CraFormData {
    applicability: Maybe<CraApplicability>;
    justification: string;
}

export interface CraState {
    formData: CraFormData;
    sectionStatus: SectionStatus;
    evidence: EvidenceItem[];
    comments: CommentItem[];
    savedOnce: boolean;
}

export const COMPLETENESS_OPTIONS = [
    'Open',
    'In progress',
    'Completed',
] as const;

export type CompletenessValue = (typeof COMPLETENESS_OPTIONS)[number];

export interface RiskAssessmentFileRow {
    id: string;
    fileName: string;
    fileType: string;
    uploaded: string;
    user: string;
    lastValidation: string;
    completeness: CompletenessValue;
}

export interface CraDocumentationFormData {
    hasProductVisual: boolean;
    hasRefDocuments: boolean;
    hasIntendedPurpose: boolean;
    hasSecDesignArchitecture: boolean;
    hasProcessReference: boolean;
}

export type CraDocumentationField = keyof CraDocumentationFormData;

export interface CraDocumentationChecklistItem {
    field: CraDocumentationField;
    label: string;
    required?: boolean;
    description: string;
}

export const INITIAL_CRA_DOCUMENTATION_FORM_DATA: CraDocumentationFormData = {
    hasProductVisual: false,
    hasRefDocuments: false,
    hasIntendedPurpose: false,
    hasSecDesignArchitecture: false,
    hasProcessReference: false,
};

export const CRA_DOCUMENTATION_CHECKLIST_ITEMS: readonly CraDocumentationChecklistItem[] =
    [
        {
            field: 'hasProductVisual',
            label: 'Product Visual',
            required: true,
            description: `For hardware products:

- **Mandatory**: Clear photographs or illustrations enabling unambiguous identification of the hardware product (e.g. front view with labeling, nameplate, relevant interfaces) shall be provided in line with EU CRA Annex VII.

- **Optional**: Additional images of internal components (e.g. CPU board, security chip, encrypted storage) are optional and may be provided for clarification purposes.

For other products with digital elements (PDEs):

- **Optional**: Visual documentation is optional and may be provided where deemed appropriate.`,
        },
        {
            field: 'hasRefDocuments',
            label: 'Reference documents',
            required: true,
            description: `Links or references to user and security documentation made available to the user (e.g. user manual, security feature specification) according to Annex II - User Information & Instructions.

**Optional**: Internal documents (e.g. security concept, hardening guide) may be referenced additionally, provided they are not part of the user information.`,
        },
        {
            field: 'hasIntendedPurpose',
            label: 'Intended Purpose',
            required: true,
            description: `Upload description of the intended purpose of the product, its operational context and relevant digital elements (e.g. connectivity, cloud integration, remote access) according to Article 6, Annex I - intended use and operational context.

**Optional**: The description should be sufficient to understand potential cybersecurity risks within the intended use context.`,
        },
        {
            field: 'hasSecDesignArchitecture',
            required: true,
            label: 'Security design architecture',
            description: `Upload description of the product (System under Consideration) from a cybersecurity perspective according to Annex VII - technical documentation; Annex I - security by design, including:

- relevant components
- security functions
- protection mechanisms
- dependencies between software and hardware components

**Optional**: Diagrams or architecture overviews may be used to support the description.`,
        },
        {
            field: 'hasProcessReference',
            label: 'Process Reference',
            required: true,
            description: `Provide reference the processes or standards according to which the product was developed and created (e.g. Secure Development Lifecycle, IEC 62443-4-1) according to Article 13, Annex VII - evidence of appropriate processes. (A reference alone does not replace content where substantive documentation is legally required).

**Optional**: Certificates or external audit evidence may be provided additionally.`,
        },
    ] as const;

export interface CraGeneralFormData {
    productName: string;
    productVersion: string;
    productCategory: Maybe<CraGeneralProductCategory>;
    securityResponsibleUserID: Maybe<string>;
    projectManagerUserID: Maybe<string>;
    supportPeriod: Maybe<number>;
    supportPeriodStartDate: Maybe<Date>;
    supportPeriodEndDate: Maybe<Date>;
    productLifetime: Maybe<number>;
    productLifetimeStartDate: Maybe<Date>;
    productLifetimeEndDate: Maybe<Date>;
    supportPeriodInformation: string;
}

export interface CraVulnerabilityFormData {
    seContact: string;
    seContactValidationDate: Maybe<Date>;
    customerContact: string;
    customerContactValidationDate: Maybe<Date>;
}

export const INITIAL_CRA_VULNERABILITY_FORM_DATA: CraVulnerabilityFormData = {
    seContact: '',
    seContactValidationDate: null,
    customerContact: '',
    customerContactValidationDate: null,
};

export const INITIAL_CRA_GENERAL_FORM_DATA: CraGeneralFormData = {
    productName: '',
    productVersion: '',
    productCategory: null,
    securityResponsibleUserID: null,
    projectManagerUserID: null,
    supportPeriod: null,
    supportPeriodStartDate: null,
    supportPeriodEndDate: null,
    productLifetime: null,
    productLifetimeStartDate: null,
    productLifetimeEndDate: null,
    supportPeriodInformation: '',
};

interface ProductCategoryOption {
    label: string;
    value: CraGeneralProductCategory;
}

export const CRA_GENERAL_PRODUCT_CATEGORIES: readonly ProductCategoryOption[] =
    [
        {
            label: 'Default Product',
            value: CraGeneralProductCategory.DefaultProduct,
        },
        {
            label: 'Important Product Class I',
            value: CraGeneralProductCategory.ImportantProductClassI,
        },
        {
            label: 'Important Product Class II',
            value: CraGeneralProductCategory.ImportantProductClassIi,
        },
        {
            label: 'Critical Product',
            value: CraGeneralProductCategory.CriticalProduct,
        },
    ] as const;
